import { useSessions, VAULT_TAB, type SessionKind } from "../vault/sessions";
import { useT } from "../i18n";
import { Icons } from "./icons";
import type { LucideIcon } from "lucide-react";

// Workspace tab strip (phase 2). The "Vault" home tab is always present and not closeable;
// each live SSH/RDP session (or a web-only placeholder) is a closeable tab with a live indicator.
const KIND_ICON: Record<SessionKind, LucideIcon> = {
  ssh: Icons.ssh,
  rdp: Icons.rdp,
  placeholder: Icons.ssh,
  "brokered-ssh": Icons.ssh,
  "brokered-rdp": Icons.rdp,
  "brokered-web": Icons.rdp,
};

export function SessionTabs() {
  const t = useT();
  const tabs = useSessions((s) => s.tabs);
  const activeKey = useSessions((s) => s.activeKey);
  const setActive = useSessions((s) => s.setActive);
  const close = useSessions((s) => s.close);

  if (tabs.length === 0) return null;

  return (
    <div className="flex items-stretch gap-1 overflow-x-auto border-b border-border bg-panel2 px-2">
      <Tab label={t("Vault")} icon={Icons.vaultOrg} active={activeKey === VAULT_TAB} onClick={() => setActive(VAULT_TAB)} />
      {tabs.map((tab) => (
        <Tab
          key={tab.key}
          label={tab.title}
          icon={KIND_ICON[tab.kind]}
          live={tab.kind !== "placeholder"}
          active={activeKey === tab.key}
          onClick={() => setActive(tab.key)}
          onClose={() => close(tab.key)}
        />
      ))}
    </div>
  );
}

function Tab({
  label,
  icon: Icon,
  live,
  active,
  onClick,
  onClose,
}: {
  label: string;
  icon: LucideIcon;
  live?: boolean;
  active: boolean;
  onClick: () => void;
  onClose?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`flex max-w-48 cursor-pointer items-center gap-1.5 border-b-2 px-2 py-1.5 text-xs ${
        active ? "border-accent text-fg" : "border-transparent text-muted hover:text-fg"
      }`}
    >
      {live ? (
        <span className="live-dot h-1.5 w-1.5 shrink-0 rounded-full bg-live" title="Connected" />
      ) : null}
      <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
      <span className="truncate">{label}</span>
      {onClose && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="ml-0.5 grid h-4 w-4 shrink-0 place-items-center rounded text-muted hover:bg-panel hover:text-fg"
          aria-label="Close session"
        >
          <Icons.close className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
